import React from 'react';
import uuid from 'uuid';
import { cloneDeep } from 'lodash';
import { FC, useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { DragDropContext, Draggable, Droppable, DropResult, DraggableLocation } from 'react-beautiful-dnd';
import { createUseStyles } from 'react-jss';
import classNames from 'classnames';
import { FormComponent } from '@web/administration/components/worksheet';
import { DetailView, DetailViewBody, DetailViewContent } from '@web/components/detail-view';
import { Label, TextInputHelper, Icon, Typography } from '@packages/ui/shared';
import {
    DraggableWorksheetFormComponent,
    draggableFormElements,
    WorkSheetStatusEnum,
    ConditionalElement,
    FormElementAction,
    FormElementTypeId,
    WorksheetElementAnswers,
} from '@packages/models/api';
import AsyncPage from '@web/components/async-page';
import { addQuestionNumber, deleteDraggableFormComponent, updateDraggableFormComponent } from '../components/utils';
import { assignElementGridIds } from '../utils';
import { FormElementModalProvider } from '@packages/contexts/form-element-modal';
import { mergeAnswerFileIntoElements } from './utils/update-form-element-file';
import { TextareaHelper } from '@packages/ui/shared/textarea-helper';
import { ToastNotification } from '@web/components/toast-notification';
import { useContentOverlayState } from '@web/core/hooks';
import { WorksheetAction, WorksheetMode } from '../types';
import { WorksheetFormComponentEditModal } from '../../../components/worksheet/worksheet-form-component-edit-modal';
import { WorksheetFormHeader } from '../components/worksheet-form-header';
import { WorksheetModal } from '../components/worksheet-modal';
import Loader from '@web/components/loader';
import useWorksheetForm from '../hooks/use-worksheet-form';
import useWorksheetFormDetails from './use-worksheet-form-detail.hooks';
import useWorksheetFormTranslation from '../hooks/use-worksheet-form-translation';
import useWorksheetPermission from '../hooks/use-worksheet-permission';
import WorksheetLeftSideBar from '../components/worksheet-form-left-sidebar';
import WorksheetRightSideBar, { FormRightBarType } from '../components/worksheet-form-right-sidebar';
import { useWorksheetNavigationGuard } from '@packages/ui/worksheet/hooks/use-worksheet-navigation-guard';
import { WorksheetUploadWarningModal } from '@packages/ui/worksheet/components/worksheet-upload-warning-modal';

type ModalSize = 'sm' | 'lg' | 'xl';

export const WorksheetsForm: FC = () => {
    const { modalDataTranslate, dataTranslate } = useWorksheetFormTranslation();
    const navigate = useNavigate();
    const { worksheetFormId } = useParams<{ worksheetFormId: string }>();
    const [searchParams] = useSearchParams();
    const worksheetStatus = (searchParams.get('status') as WorkSheetStatusEnum) || WorkSheetStatusEnum.Pending;
    const action = searchParams.get('action') as WorksheetAction;
    const maxVersion = parseInt(searchParams.get('maxVersion') || '1');
    const classes = useNewWorksheetFormStyles();
    const [showDeleteMessage, setShowDeleteMessage] = useState<boolean>(false);
    const [showCreateWorksheet, setShowCreateWorksheet] = useState<boolean>(false);
    const [bodyDeleteMessage, setDeleteBodyMessage] = useState<string>('');
    const [modalBodyMessage, setModalBodyMessage] = useState<string>('');
    const [deleteComponent, setDeleteComponent] = useState<DraggableWorksheetFormComponent | ConditionalElement>();
    const [createdWorksheet, setCreatedWorksheet] = useState<boolean>(false);
    const [showFormComponentEditModal, setShowFormComponentEditModal] = useState<{ show: boolean; size: ModalSize }>({
        show: false,
        size: 'lg',
    });
    const [formComponentToEdit, setFormComponentToEdit] = useState<DraggableWorksheetFormComponent>();
    const [exit, setExit] = useState(false);
    const [isLoading, setIsLoading] = useState({ saveAndExit: false, save: false });
    const [showToast, setShowToast] = useState({ show: false, message: '', variant: 'success' as 'success' | 'error' });
    const [previousDescription, setPreviousDescription] = useState<string>('');
    const { toggleDraggable } = useContentOverlayState();
    const { isEditOnlyPermission, isCreateOnlyPermission } = useWorksheetPermission();

    const {
        elements,
        fetching,
        setWorkSheetInfo,
        worksheetInfo,
        worksheetDisplayVersion,
        setElements,
        actions,
        worksheetSaveAndExit,
        saveState,
    } = useWorksheetFormDetails({
        worksheetFormId: worksheetFormId,
        worksheetAction: action,
        maxVersion,
    });

    const handleInlineUpdateError = useCallback((message: string) => {
        setShowToast({ show: true, message, variant: 'error' });
    }, []);

    const { state, actions: formActions } = useWorksheetForm({
        setWorkSheetInformation: setWorkSheetInfo,
        worksheetInfo: worksheetInfo,
        worksheetAction: action,
        onInlineUpdateError: handleInlineUpdateError,
    });

    const { handleNameChange, handleTitleChange, handleDescriptionChange } = formActions;

    // Navigation guard to warn about uploads in progress
    const {
        hasUploadsInProgress,
        showNavigationModal,
        warningMessage,
        handleConfirmNavigation,
        handleCancelNavigation,
    } = useWorksheetNavigationGuard(elements);

    useEffect(() => {
        toggleDraggable(true);
        return () => toggleDraggable(false);
    }, []);

    // Initialize previousDescription only on initial worksheet load
    useEffect(() => {
        if (worksheetInfo && !fetching && previousDescription === '') {
            setPreviousDescription(worksheetInfo.description || '');
        }
    }, [worksheetInfo, fetching, previousDescription]);

    const handleSaveAndExit = useCallback(
        async (isExit: boolean) => {
            try {
                // Validate all required fields
                const hasValidationErrors =
                    !worksheetInfo.displayname ||
                    !worksheetInfo.worksheetGroupName ||
                    !worksheetInfo.worksheetCaseGroupId ||
                    (!worksheetInfo.caseTypeId && action === WorksheetAction.New);

                if (hasValidationErrors) {
                    // Trigger validation for all fields
                    handleTitleChange(worksheetInfo.displayname || '');
                    handleNameChange(worksheetInfo.worksheetGroupName || '');
                    formActions.handleChangeWorksheetGroup?.(worksheetInfo.worksheetCaseGroupId || '');
                    if (action === WorksheetAction.New) {
                        formActions.handleChangeCaseType?.(worksheetInfo.caseTypeId || '');
                    }
                    return;
                }
                setExit(isExit);
                await worksheetSaveAndExit({
                    setModalBodyMessage,
                    setCreatedWorksheet,
                    setIsLoading,
                    setShowCreateWorksheet,
                    isExit,
                    mode: WorksheetMode.Edit,
                });
                // Update previousDescription on successful save
                setPreviousDescription(worksheetInfo.description || '');
            } catch (error) {
                // Revert to previous description if update fails
                handleDescriptionChange(previousDescription);
                setShowToast({
                    show: true,
                    message: 'Unable to update worksheet description. Please try again.',
                    variant: 'error',
                });
            }
        },
        [
            worksheetInfo.displayname,
            worksheetInfo.worksheetGroupName,
            worksheetInfo.worksheetCaseGroupId,
            worksheetInfo.caseTypeId,
            action,
            worksheetSaveAndExit,
            handleTitleChange,
            handleNameChange,
            handleDescriptionChange,
            previousDescription,
            formActions,
        ]
    );

    const addNewFormComponent = useCallback(
        (source: DraggableLocation, destination: DraggableLocation) => {
            let formComponentsClone = cloneDeep(elements);
            const draggedFormElementClone = cloneDeep(Object.values(draggableFormElements)[source.index]);
            const newFormComponent = Object.assign(draggedFormElementClone, { formElementId: uuid() });
            if (newFormComponent.options) {
                newFormComponent.options = newFormComponent.options.map((option, index) => {
                    option.optionId = uuid();
                    option.optionValue = String(index);
                    return option;
                });
            } else {
                newFormComponent.options = [];
            }

            assignElementGridIds(newFormComponent.grid);

            newFormComponent.action = FormElementAction.Insert;
            newFormComponent.formElementOrder = destination.index + 1;
            formComponentsClone.splice(destination.index, 0, newFormComponent);
            formComponentsClone = addQuestionNumber(formComponentsClone);
            setElements(formComponentsClone);
        },
        [elements, setElements]
    );

    const reorderFormComponents = useCallback(
        (source: DraggableLocation, destination: DraggableLocation) => {
            let formComponentsClone = cloneDeep(elements);
            const [draggedFormComponent] = formComponentsClone.splice(source.index, 1);
            formComponentsClone.splice(destination.index, 0, draggedFormComponent);
            formComponentsClone = addQuestionNumber(formComponentsClone);
            setElements(formComponentsClone);
        },
        [elements, setElements]
    );

    const handleDragEnd = useCallback(
        (result: DropResult) => {
            const { source, destination } = result;
            if (!destination) {
                return;
            }
            switch (source.droppableId) {
                case 'FORM_ELEMENTS':
                    addNewFormComponent(source, destination);
                    break;
                case destination.droppableId:
                    reorderFormComponents(source, destination);
                    break;
                default:
                    return;
            }
        },
        [addNewFormComponent, reorderFormComponents]
    );

    const handleFormElementAnswerChange = useCallback((answer: WorksheetElementAnswers) => {
        if (!answer?.elementId || !answer.asset?.answerFile) {
            return;
        }
        setElements((prevElements) => {
            const { updatedElements, changed } = mergeAnswerFileIntoElements(prevElements, answer);
            return changed ? updatedElements : prevElements;
        });
    }, []);

    const handleCopyFormComponents = useCallback(
        (item: ConditionalElement | DraggableWorksheetFormComponent) => {
            let formComponentsClone = cloneDeep(elements);
            const copyElementClone = cloneDeep(item);
            copyElementClone.formElementId = uuid();
            if (copyElementClone.optionConditionsElements?.length) {
                const elementJson = copyElementClone.optionConditionsElements.reduce(
                    (_json: { [x: string]: string }, element) => {
                        _json[element.formElementId] = uuid();
                        return _json;
                    },
                    {}
                );
                copyElementClone.options.forEach((opt) => {
                    elementJson[opt.optionId] = uuid();
                    opt.optionId = elementJson[opt.optionId];
                    if (opt.trgtElementIdList?.length) {
                        opt.trgtElementIdList = opt.trgtElementIdList.map((trgId) => elementJson[trgId]);
                    }
                });
                copyElementClone.optionConditionsElements.forEach((ele) => {
                    ele.formElementId = elementJson[ele.formElementId];
                    ele.targetOptionId = elementJson[ele.targetOptionId];
                    if (ele.options.length) {
                        ele.options = ele.options.map((opt) => {
                            opt.optionId = uuid();
                            return opt;
                        });
                        assignElementGridIds(ele.grid);
                    }
                });
            } else {
                copyElementClone.options.forEach((opt) => {
                    opt.optionId = uuid();
                });
                assignElementGridIds(copyElementClone.grid);
            }
            copyElementClone.action = FormElementAction.Insert;
            copyElementClone.formElementOrder = item.formElementOrder || 0;

            if ('targetOptionId' in copyElementClone) {
                formComponentsClone.forEach((component) => {
                    if ([FormElementTypeId.Checkbox, FormElementTypeId.Radio].includes(component.formElementTypeId)) {
                        const targetOptionIndex = component.options.findIndex(
                            (option) => option.optionId === copyElementClone.targetOptionId
                        );
                        if (targetOptionIndex !== -1) {
                            component.options[targetOptionIndex].trgtElementIdList?.push(
                                copyElementClone.formElementId
                            );
                            component.optionConditionsElements?.splice(
                                copyElementClone.formElementOrder || component.optionConditionsElements.length,
                                0,
                                copyElementClone
                            );
                        }
                    }
                });
            } else {
                formComponentsClone.splice(copyElementClone.formElementOrder, 0, copyElementClone);
            }
            formComponentsClone = addQuestionNumber(formComponentsClone);
            setElements(formComponentsClone);
        },
        [elements, setElements]
    );

    const handleFormComponentEditClick = useCallback((item: DraggableWorksheetFormComponent) => {
        setFormComponentToEdit(item);
        const size = item.formElementTypeId === FormElementTypeId.DynamicGrid ? 'xl' : 'lg';
        setShowFormComponentEditModal({ show: true, size });
    }, []);

    const handleFormComponentEditModalHide = useCallback(() => {
        setShowFormComponentEditModal((prev) => ({ ...prev, show: false }));
        setFormComponentToEdit(undefined);
    }, []);

    const handleFormComponentEditModalSave = useCallback(
        (item: DraggableWorksheetFormComponent | ConditionalElement) => {
            let formComponentsClone = cloneDeep(elements);
            formComponentsClone = updateDraggableFormComponent(formComponentsClone, item);
            formComponentsClone = addQuestionNumber(formComponentsClone);
            setElements(formComponentsClone);
            handleFormComponentEditModalHide();
        },
        [elements, handleFormComponentEditModalHide, setElements]
    );

    const handleFormComponentDeleteClick = useCallback(
        (item: DraggableWorksheetFormComponent) => {
            setShowDeleteMessage(true);
            setDeleteBodyMessage(modalDataTranslate.deleteElement.bodyMessage(item.description));
            setDeleteComponent(item);
        },
        [modalDataTranslate.deleteElement]
    );

    const handleDeleteModal = useCallback(
        (ans: boolean) => {
            if (!deleteComponent) {
                return;
            }
            if (ans) {
                let formComponentsClone = cloneDeep(elements);
                formComponentsClone = deleteDraggableFormComponent(formComponentsClone, deleteComponent);
                formComponentsClone = addQuestionNumber(formComponentsClone);
                setElements(formComponentsClone);
            }
            setShowDeleteMessage(false);
        },
        [deleteComponent, elements, setElements]
    );

    const handlePreview = useCallback(() => {
        saveState(worksheetInfo, elements);
        worksheetFormId
            ? navigate(
                  `/administration/worksheet/${worksheetFormId}?action=${action}&status=${worksheetStatus}${
                      action === WorksheetAction.AddVersion ? `&maxVersion=${maxVersion}` : ''
                  }`
              )
            : navigate(
                  `/administration/worksheet/1?action=${WorksheetAction.New}&status=${
                      worksheetStatus || WorkSheetStatusEnum.Pending
                  }`
              );
    }, [action, elements, maxVersion, navigate, saveState, worksheetFormId, worksheetInfo, worksheetStatus]);

    if (!(isEditOnlyPermission || isCreateOnlyPermission)) {
        navigate(
            `/administration/worksheet/${worksheetFormId}?action=${action}&status=${worksheetStatus}${
                action === WorksheetAction.AddVersion ? `&maxVersion=${maxVersion}` : ''
            }`
        );
    }
    return (
        <AsyncPage fetchData={actions.fetchData}>
            {fetching ? (
                <Loader />
            ) : (
                <>
                    <WorksheetModal
                        show={showDeleteMessage}
                        onHideModal={() => setShowDeleteMessage(false)}
                        title={modalDataTranslate.deleteElement.title}
                        warningMessage={bodyDeleteMessage}
                        handleAction={handleDeleteModal}
                        cancelButton={modalDataTranslate.deleteElement.cancelButton}
                        confirmButton={modalDataTranslate.deleteElement.confirmButton}
                    />
                    <WorksheetModal
                        show={showCreateWorksheet}
                        title={
                            worksheetFormId
                                ? modalDataTranslate.createOrUpdateWorksheet.title.update
                                : modalDataTranslate.createOrUpdateWorksheet.title.create
                        }
                        onHideModal={() => {
                            if (exit) {
                                navigate('/administration/worksheets');
                            }
                            setShowCreateWorksheet(false);
                        }}
                        bodyMessage={modalBodyMessage}
                        confirmButton="Ok"
                        handleAction={() => {
                            if (createdWorksheet && exit) {
                                navigate('/administration/worksheets');
                            }
                            setShowCreateWorksheet(false);
                        }}
                        closeButton={false}
                    />
                    <FormElementModalProvider>
                        <WorksheetFormComponentEditModal
                            formComponent={formComponentToEdit}
                            show={showFormComponentEditModal.show}
                            size={showFormComponentEditModal.size}
                            onSave={handleFormComponentEditModalSave}
                            onHide={handleFormComponentEditModalHide}
                            key={formComponentToEdit?.formElementId}
                        />

                        <DetailView>
                            <WorksheetFormHeader
                                handlePreview={handlePreview}
                                handleSaveAndExit={handleSaveAndExit}
                                worksheetInfo={worksheetInfo}
                                worksheetDisplayVersion={worksheetDisplayVersion}
                                worksheetFormId={worksheetFormId}
                                isLoading={isLoading}
                                worksheetStatus={worksheetStatus}
                                isCaseTypeLoading={formActions.isCaseTypeLoading}
                                isPreview={false}
                            />
                            <DetailViewContent>
                                <DetailViewBody className="pt-4 pb-3 pr-0 pl-6 overflow-hidden">
                                    <DragDropContext onDragEnd={handleDragEnd}>
                                        <div className={classes.formRow}>
                                            <WorksheetLeftSideBar classes={classes} />
                                            <div className={classes.formComponentsCol}>
                                                <div className={classes.formComponentsScrollArea}>
                                                    <div className="mr-4">
                                                        <div className="mb-2">
                                                            <Label required>
                                                                {dataTranslate.adminFormBuilder.inputs.formTitle.label}
                                                            </Label>
                                                        </div>
                                                        <div className="mb-6">
                                                            <TextInputHelper
                                                                required
                                                                errorMessage={state.titleErrorMessage}
                                                                value={worksheetInfo.displayname}
                                                                onChangeText={(title) => handleTitleChange(title)}
                                                                placeholder={
                                                                    dataTranslate.adminFormBuilder.inputs.formTitle
                                                                        .placeholder
                                                                }
                                                            />
                                                        </div>
                                                        <div className="mb-2">
                                                            <Label>
                                                                {
                                                                    dataTranslate.adminFormBuilder.inputs
                                                                        .worksheetDescription.label
                                                                }
                                                            </Label>
                                                        </div>
                                                        <div className="mb-6">
                                                            <TextareaHelper
                                                                value={worksheetInfo.description || ''}
                                                                onChangeText={(description) => {
                                                                    if (description.length > 400) {
                                                                        handleDescriptionChange(
                                                                            description.substring(0, 400)
                                                                        );
                                                                    } else {
                                                                        handleDescriptionChange(description);
                                                                    }
                                                                }}
                                                                placeholder={
                                                                    dataTranslate.adminFormBuilder.inputs
                                                                        .worksheetDescription.placeholder
                                                                }
                                                            />
                                                            <div
                                                                style={{
                                                                    display: 'flex',
                                                                    justifyContent: 'space-between',
                                                                    marginTop: 2,
                                                                }}
                                                            >
                                                                <Typography variant="labelRegular" color="redOne">
                                                                    {worksheetInfo?.description?.length &&
                                                                    worksheetInfo?.description?.length > 0 &&
                                                                    worksheetInfo?.description?.trim() === ''
                                                                        ? 'Description cannot be empty or just spaces'
                                                                        : ''}
                                                                </Typography>
                                                                <Typography
                                                                    variant="labelRegular"
                                                                    color="textDarkSecondary"
                                                                >
                                                                    {worksheetInfo?.description?.length}/400
                                                                </Typography>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <Droppable droppableId="FORM_COMPONENTS">
                                                        {(provided, snapshot) => (
                                                            <div
                                                                ref={provided.innerRef}
                                                                {...provided.droppableProps}
                                                                className={classNames({
                                                                    [classes.formContainer]: true,
                                                                    [classes.formContainerIsDraggingOver]:
                                                                        snapshot.isDraggingOver,
                                                                })}
                                                            >
                                                                {elements && elements?.length ? (
                                                                    elements.map((element, index) => (
                                                                        <Draggable
                                                                            key={element.formElementId}
                                                                            draggableId={element.formElementId}
                                                                            index={index}
                                                                        >
                                                                            {(provided, snapshot) => (
                                                                                <FormComponent
                                                                                    provided={provided}
                                                                                    snapshot={snapshot}
                                                                                    item={element}
                                                                                    parentItem={element}
                                                                                    editIconName="pencil"
                                                                                    deleteIconName="trash"
                                                                                    copyIconName="copy"
                                                                                    draggableIcon="draggable"
                                                                                    onAnswerChange={
                                                                                        handleFormElementAnswerChange
                                                                                    }
                                                                                    onChildDrag={
                                                                                        handleFormComponentEditModalSave
                                                                                    }
                                                                                    onEditClick={
                                                                                        handleFormComponentEditClick
                                                                                    }
                                                                                    onDeleteClick={
                                                                                        handleFormComponentDeleteClick
                                                                                    }
                                                                                    onCopyClick={
                                                                                        handleCopyFormComponents
                                                                                    }
                                                                                />
                                                                            )}
                                                                        </Draggable>
                                                                    ))
                                                                ) : (
                                                                    <div className={classes.dropNotice}>
                                                                        <p className="mb-0">
                                                                            {dataTranslate.adminFormBuilder.dropArea}
                                                                        </p>
                                                                    </div>
                                                                )}
                                                                {provided.placeholder}
                                                            </div>
                                                        )}
                                                    </Droppable>
                                                </div>
                                            </div>
                                        </div>
                                    </DragDropContext>
                                </DetailViewBody>

                                <WorksheetRightSideBar
                                    worksheetInfo={worksheetInfo}
                                    state={state}
                                    actions={formActions}
                                    type={FormRightBarType.Form}
                                    isLoading={isLoading}
                                />
                            </DetailViewContent>
                        </DetailView>
                    </FormElementModalProvider>
                </>
            )}

            <ToastNotification
                show={showToast.show}
                onClose={() => setShowToast({ ...showToast, show: false })}
                body={
                    <div className="d-flex align-items-center justify-content-center gap-3">
                        <Icon name={showToast.variant === 'success' ? 'checkmark' : 'error'} size={20} color="white" />
                        <Typography variant="h6" color="white">
                            {showToast.message}
                        </Typography>
                    </div>
                }
                autoHideTime={3000}
                variant={showToast.variant}
                position="top-center"
            />

            {/* Navigation warning modal for uploads in progress */}
            <WorksheetUploadWarningModal
                visible={showNavigationModal}
                onConfirm={handleConfirmNavigation}
                onCancel={handleCancelNavigation}
                message={warningMessage}
            />
        </AsyncPage>
    );
};

const useNewWorksheetFormStyles = createUseStyles({
    formRow: {
        display: 'flex',
    },
    formElementsCol: {
        width: 192,
        flexShrink: 0,
        marginRight: 16,
        maxHeight: '77vh',
        overflowY: 'scroll',
    },
    formComponentsCol: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
    },
    formComponentsScrollArea: {
        flex: 1,
        maxHeight: '77vh',
        overflowY: 'auto',
    },
    formContainer: {
        flex: 1,
        paddingRight: '16px',
        minHeight: '200px',
    },
    formContainerIsDraggingOver: {},
    dropNotice: {
        padding: 32,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    modal: {
        top: '105px',
        left: '384px',
    },
});

export type UseNewWorksheetFormStylesReturn = ReturnType<typeof useNewWorksheetFormStyles>;
